import ConsultingHero from "../shared/ConsultingHero/ConsultingHero";
import ServiceIntro from "../shared/ServiceIntro/ServiceIntro";
import ImageContentSection from "../shared/ImageContentSection/ImageContentSection";
import ServiceCardsSection from "../shared/ServiceCardsSection/ServiceCardsSection";
import ProcessDiagram from "../shared/ProcessDiagram/ProcessDiagram";
import CTABanner from "../shared/CTABanner/CTABanner";
import AssociatedServices from "../shared/AssociatedServices/AssociatedServices";
import FAQ from "../shared/FAQ/FAQ";
import TestimonialsSection from "../shared/TestimonialsSection/TestimonialsSection";
import LinkButton from "../shared/Buttons/LinkButton";
import ScreenshotScroll from "../shared/ScreenshotScroll/ScreenshotScroll";
import { technologyWhyItMatters, technologyWhyTrustUs, technologyServiceCards, technologyAssociatedServices, technologyFaqs, testimonials } from "./data/consultingData";

const TechnologyService = () => {
  return (
    <div>
      <ConsultingHero
        title={
          <>
            <span className="block">Build a website</span>
            <span className="block">that grows your</span>
            <span className="block">mission</span>
          </>
        }
        buttonText="Book a Free Consultation"
        buttonTo="/contact-us"
        buttonColour="orange"
        button2Text="See Our Rescue Website Work"
        button2To="/contact-us"
        button2Colour="blue"
        bgColor="bg-blue"
        imageSrc="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/dalmation4-480.webp"
        imageSrcSet="
          https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/dalmation4-480.webp 480w,
          https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/dalmation4-1280.webp 1280w,
          https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/dalmation4-1920.webp 1920w
        "
        imageAlt="A dalmatian dog"
      />

      <ServiceIntro
        title="Your mission deserves a better website"
        description="We design and develop websites that help animal rescues grow — increasing adoptions, attracting donors, and simplifying your team's day-to-day management."
        rightCircleSrc="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/right-circle-group.svg"
      />

      <ImageContentSection
        imagePosition="right"
        imageSrc={technologyWhyItMatters.imageSrc}
        imageAlt={technologyWhyItMatters.imageAlt}
        title={technologyWhyItMatters.title}
        description={technologyWhyItMatters.description}
        items={technologyWhyItMatters.items}
        imageBg="bg-blue"
      />

      <ServiceCardsSection
        bgColor={technologyServiceCards.bgColor}
        title={technologyServiceCards.title}
        description={technologyServiceCards.description}
        cardBg={technologyServiceCards.cardBg}
        items={technologyServiceCards.items}
      />

      <ImageContentSection
        imagePosition="left"
        imageSrc={technologyWhyTrustUs.imageSrc}
        imageAlt={technologyWhyTrustUs.imageAlt}
        title={technologyWhyTrustUs.title}
        description={technologyWhyTrustUs.description}
        items={technologyWhyTrustUs.items}
        imageBg="bg-orange"
        imageClassName="object-right"
      />

      <ProcessDiagram
        title="Our 4-step website build process"
        description="With hundreds of nonprofit builds behind us, we've refined a process that's clear, transparent & built around your rescue's goals. Here's how we turn your ideas into a finished website."
        svgSrc="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/timeline3.svg"
        svgAlt="Our 4-step website build process diagram"
      />

      {/* Wireframe to launch section */}
      <section className="px-4 md:px-8 lg:px-16 xl:px-24 my-16 lg:my-24 xl:my-52">
        <div className="max-w-7xl mx-auto">

          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center gap-8 mb-12">
            <div className="md:w-1/2">
              <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-blue-dark1 mb-4">
                From wireframe to launch
              </h2>
              <p className="!text-sm md:!text-base xl:!text-lg leading-relaxed">
                Every rescue website starts with clear prototypes — so you can visualize the finished design before we write a single line of code.
              </p>
            </div>
            <div className="md:w-1/2 flex justify-center md:justify-end">
              <img
                src="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/starski-laptop.webp"
                alt="Starski at a laptop"
                className="w-48 md:w-64 xl:w-80"
                loading="lazy"
              />
            </div>
          </div>

          {/* Image cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            {[
              { src: "https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/1-wireframe-to-launch-design-system.png", alt: "Design system" },
              { src: "https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/2-wireframe-to-launch-lofi.png", alt: "Lo-fi wireframe" },
              { src: "https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/3-wireframe-to-launch-hifi.png", alt: "Hi-fi design" },
            ].map((img, i) => (
              <div key={i} className="border border-gray-200 rounded-2xl h-56 md:h-64 xl:h-80 overflow-hidden bg-white">
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            ))}
          </div>

          {/* CTA button */}
          <div className="flex justify-start">
            <LinkButton to="/contact-us" colour="blue">
              Request a Free Design Mockup
            </LinkButton>
          </div>

        </div>
      </section>

      {/* See what modern nonprofit websites look like section */}
      <section className="bg-orange py-16 px-4 md:px-8 lg:px-16 xl:px-24 my-16 lg:my-24 xl:my-52">
        <div className="max-w-7xl mx-auto">

          {/* Header */}
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-blue-navy mb-4">
              See what modern nonprofit websites look like
            </h2>
            <p className="!text-sm md:!text-base xl:!text-lg leading-relaxed max-w-md mx-auto">
              From small rescues to national foundations, we've built sites that combine storytelling, accessibility & performance.
            </p>
          </div>

          {/* 2x2 screenshot scroll grid */}
          <div className="grid grid-cols-2 gap-4 md:gap-6 mb-10">
            {[
              { src: "https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/straydog.png", alt: "Stray Dog website" },
              { src: "https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/guardian-pet-trust.png", alt: "Guardian Pet Trust website" },
              { src: "https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/silver-linings.png", alt: "Silver Linings website" },
              { src: "https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/humane-society-of-nw-montana.png", alt: "Humane Society of NW Montana website", duration: 15 },
            ].map((screenshot, i) => (
              <div key={i} className="rounded-2xl overflow-hidden border border-gray-200">
                <ScreenshotScroll
                  src={screenshot.src}
                  alt={screenshot.alt}
                  windowHeight={320}
                  duration={screenshot.duration ?? 10}
                />
              </div>
            ))}
          </div>

          {/* CTA button */}
          <div className="flex justify-center">
            <LinkButton to="/contact-us" colour="blue">
              Discover Our Work
            </LinkButton>
          </div>

        </div>
      </section>

      <TestimonialsSection
        bgColor="bg-blue/20"
        cardBg="bg-white"
        showHorizontalLine={false}
        title={
          <>
            See how rescues are growing with Sponsor a Pet websites
          </>
        }
        description="From small rescues to national foundations, we've built sites that combine storytelling and performance."
        testimonials={testimonials}
        images={[
          { src: "https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/dalmation6.webp", alt: "A dalmatian dog", bg: "bg-blue" },
          { src: "https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/dalmation1.webp", alt: "A dalmatian dog", bg: "bg-blue" },
          { src: "https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/dalmation8.webp", alt: "A dalmatian dog", bg: "bg-blue" },
        ]}
      />

      <CTABanner
        bgColor="bg-orange"
        overlayFrom="from-orange via-orange/80"
        imageSrc="https://sap-app-prod.s3.us-west-2.amazonaws.com/consulting-services/consulting-pages-v2/dalmation2.webp"
        imageAlt="A dalmatian dog"
        title="Let's build a website that grows your impact"
        description="Empower your rescue with a site that turns visitors into supporters."
        buttonText="Book a Free Consultation"
        buttonTo="/contact-us"
        buttonColour="blue"
        button2Text="Get a Free Website Audit"
        button2To="/contact-us"
        button2Colour="blue"
      />

      <AssociatedServices
        subtitle="More ways to grow your audience."
        services={technologyAssociatedServices}
        cols={2}
      />

      <FAQ subtitle="About Website Building" faqs={technologyFaqs} />
    </div>
  );
};

export default TechnologyService;
